<template>
  <wdw-dynbox
    v-if="suggestionList && suggestionList.length > 0"
    :title="domainStore.translate('thought.suggestions.title')"
    :config="{ showControls: true, contentClass: 'thought-suggestions' }"
    :minimize="false"
  >
    <p>
      {{ domainStore.translate('thought.suggestions.message.info') }}
    </p>
    <div v-for="suggestion in suggestionList" v-bind:key="suggestion.id">
      <thought-list-wrapper :config="config" :thought="suggestion" @open-thought="emitOpenThought" />
    </div>
  </wdw-dynbox>
</template>

<script>
import ThoughtListWrapper from '@/components/discuss/thought/ListWrapper.vue';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import { RepositoryFactory } from '@/api/repositoryFactory';
import { thoughtStore } from '@/store/thought-store';
import { domainStore } from '@/store/domain-store';
import useThoughtEvents from '@/composables/thought/useThoughtEvents';
import useThoughtConfig from '@/composables/thought/useThoughtConfig';
import { ref, toRefs, watch, computed } from 'vue';

const TopicRepository = RepositoryFactory.get('topic');

export default {
  name: 'ThoughtSuggestions',

  props: {
    topic: {
      type: Object,
      required: false,
    },
    query: {
      type: String,
      required: true,
    },
    config: {
      type: Object,
      required: false,
      default: () => {
        return {
          disableOpening: true,
          disableEditing: true,
          hideVoting: false,
          hideComments: true,
        };
      },
    },
  },

  setup(props, context) {
    const { emitOpenThought, emitEditThought } = useThoughtEvents(context);
    const { canEditThought } = useThoughtConfig();
    // ---VARIABLES----
    const { query } = toRefs(props);
    const suggestionList = ref(0);
    const isLoadingSuggestionRequest = ref(false);
    let abortController = new AbortController(); // For aborting previous request
    const APICALL_DELAY_MS = 1000; // After user has paused for 1s, then the API call can commence
    let timeoutIdx = 0;

    // --------------------COMPUTED------------------
    const thoughts = computed(() => {
      return thoughtStore.getVisibleThoughts();
    });

    // ---------------OTHER METHODS------------------
    const fetchSuggestions = function () {
      if (query.value.length > 2) {
        // Loading
        if (isLoadingSuggestionRequest.value) {
          abortController.abort("new request coming with query '" + query.value + "'");
        }
        // Try to abort previous request if it's still ongoing
        // Since we have new query input, we should create a new request instead
        abortController = new AbortController();

        // Search for the appropriate title when the user enters a text
        isLoadingSuggestionRequest.value = true;
        TopicRepository.getSuggestions(props.topic.id, query.value, abortController)
          .then(response => {
            suggestionList.value = response;
            isLoadingSuggestionRequest.value = false;
          })
          .catch(err => {
            // Error sending vote (or cancelled due to new request taking precedence)
            isLoadingSuggestionRequest.value = false;
          });
      }
    };

    // ---WATCH---
    // Watch on query to update the title suggestions to correspond to the user's input
    watch(query, (newPropsQuery, prevPropsQuery) => {
      // Clear previous delay
      clearTimeout(timeoutIdx);
      // Create new delay
      timeoutIdx = setTimeout(() => {
        fetchSuggestions();
      }, APICALL_DELAY_MS);
    });
    return {
      domainStore,
      suggestionList,
      emitOpenThought,
      emitEditThought,
      canEditThought,
    };
  },
  components: {
    ThoughtListWrapper,
    WdwDynbox,
  },
};
</script>

<style scoped></style>
